import { Offer, Building, Equipment } from 'app/types/types'
import { t } from 'i18next'
import * as R from 'ramda'
import { getEquipmentContractDate } from 'app/app/common/common-utils'

const ACCEPTABLE_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'image/jpeg',
  'image/png'
]

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB in bytes

export const validateFileUpload = (file: File, existingFiles: File[]): { isValid: boolean; error?: string } => {
  // Check if file already exists
  if (existingFiles.find(f => f.name === file.name)) {
    return { isValid: false, error: t('contract.refresh.fileAlreadyAdded') }
  }

  // Check file type
  if (!ACCEPTABLE_FILE_TYPES.includes(file.type)) {
    return {
      isValid: false,
      error: t('contract.refresh.invalidFileType')
    }
  }

  // Check total file size
  const totalSize = existingFiles.reduce((sum, f) => sum + f.size, 0) + file.size
  if (totalSize > MAX_FILE_SIZE) {
    return { isValid: false, error: t('contract.refresh.fileSizeExceeds') }
  }

  return { isValid: true }
}

export const mapUpdatedOfferDetails = (
  currentOffer: Offer,
  mappedFields: Array<{ KCC_EQUNR: string; EQUNR: string; NAME: string }>,
  status: string
): Offer => {
  const mapEquipment = (equipment: Equipment) => {
    const newMapping = mappedFields.find(
      (field: { KCC_EQUNR: string; EQUNR: string }) => field.KCC_EQUNR === equipment.salesToolId
    )
    return newMapping ? { ...equipment, autoEquipmentCreated: true, koneEquipmentNumber: newMapping.EQUNR } : equipment
  }

  const mapBuilding = (building: Building) => ({
    ...building,
    details: {
      ...building.details,
      equipment: building.details.equipment.map(mapEquipment)
    }
  })

  return {
    ...currentOffer,
    buildings: currentOffer.buildings.map(mapBuilding),
    isEquipmentCreated: status
  }
}

export const isContractEnabled = (offer: Offer): boolean => {
  if (R.isNil(offer) || R.isEmpty(offer)) {
    return false
  }
  const { tenderVersionId, sapContractNumber, discountApprovalStatus } = offer
  return (
    (!R.isNil(sapContractNumber) || !R.isNil(tenderVersionId)) &&
    (discountApprovalStatus === 'approved' || discountApprovalStatus === 'uninitialized')
  )
}

// Flexible date utilities
export const isFlexibleStartDate = (offer: Offer) =>
  getEquipmentContractDate(offer.commercialTerms?.VBEGDAT, 'VBEGDAT') === 'flexible'

export const isEmptyStartDate = (offer: Offer) => offer.commercialTerms?.VBEGDAT === undefined

export const isFlexibleEscalationDate = (offer: Offer) =>
  offer.commercialTerms?.KCSM_BASE_MONTH_ESCALATION === 'flexible'

export const areEitherDatesFlexible = (offer: Offer) => isFlexibleStartDate(offer) || isFlexibleEscalationDate(offer)
